import { getUserMainRole } from "../hooks/useUserRoles";
import {
  REQUIRED_EULA_VERSION,
  REQUIRED_PRIVACY_VERSION,
  REQUIRED_DPA_VERSION,
} from "../constants/legal";
import { isLegalVersionSatisfied } from "../utils/legalVersion";
import type { UserResponseModel } from "../api/models/UserResponseModel";
import type { TermsUsageModel } from "../api";

// 7HAI-544: acceptance model
//  - Privacy Policy: per-user (every accepting role), recorded on the
//    privacy_policy flag. A master reset clears the flag and re-prompts.
//  - EULA: COMPANY-wide (company.license). Only an admin can accept it; a
//    non-admin user is blocked ("waiting for admin") until an admin does.
//  - master: exempt from everything — never gated.
//
// 7HAI-972: the Data Processing Agreement joins the model as a SECOND
// company-wide agreement, with exactly the EULA's shape — its own acceptance
// record, admin-only to accept, non-admin waits. Rather than duplicate the EULA
// helpers per document, the company-wide agreements are enumerated in one place
// (`companyWidePending`) and every consumer derives from it, so adding a third
// document is a one-line change and cannot leave a gate half-wired.
//
// 7HAI-968: and acceptance is VERSION-aware, not merely presence-based. These
// two generalisations are orthogonal and both apply to all three documents: a
// step is satisfied only when an acceptance EXISTS **and** the version stamped
// on it is at or above the REQUIRED_* floor in constants/legal. A missing or
// malformed stored version is older than everything and re-prompts (see
// utils/legalVersion for the ordering rules, which the backend mirrors).
//
// The previous presence-only check was a lockout waiting to happen: once the
// backend gate goes version-aware, a user below the required version has their
// session refused everywhere while this module reports nothing to accept, so
// the one screen that could fix it never renders.

export type AgreementId = "privacy" | "license" | "dpa";

// Company license JSONB shape as returned by /api/login/ (`company_license`).
export type CompanyLicenseInfo = {
  valid?: string | null;
  accepted?: string | null;
  acceptor?: string | null;
  version?: string | null;
} | null;

// 7HAI-972: `company_dpa` as returned by /api/login/ and by
// GET /api/company/{id}/dpa. The backend returns a never-accepted STUB rather
// than null when nothing is on file, so `accepted` is the field to test, not
// the object's presence.
export type CompanyDpaInfo = {
  accepted?: string | null;
  acceptor?: string | null;
  version?: string | null;
  required_version?: string | null;
  current?: boolean | null;
} | null;

// Has this user accepted the privacy policy at all, at any version? Separate
// from isPrivacyAccepted so the page can tell a first-time acceptance from a
// re-prompt caused by an updated document.
export const hasPrivacyOnRecord = (user: UserResponseModel | null): boolean => {
  const t = (user?.terms_and_usage as TermsUsageModel | undefined) ?? {};
  return t.privacy_policy === 1 || !!t.privacy_policy_accepted;
};

export const isPrivacyAccepted = (user: UserResponseModel | null): boolean => {
  if (!hasPrivacyOnRecord(user)) return false;
  const t = (user?.terms_and_usage as TermsUsageModel | undefined) ?? {};
  return isLegalVersionSatisfied(t.privacy_policy_version, REQUIRED_PRIVACY_VERSION);
};

// EULA is company-wide: accepted when the company has an acceptance on record
// at or above the required version.
export const isCompanyEulaAccepted = (companyLicense: CompanyLicenseInfo): boolean =>
  !!companyLicense?.accepted &&
  isLegalVersionSatisfied(companyLicense.version, REQUIRED_EULA_VERSION);

// The DPA is company-wide too, and takes the same presence-AND-version test.
//
// 7HAI-968: the floor is OUR REQUIRED_DPA_VERSION constant — the mirror of the
// backend's — and NOT the record's own `current` / `required_version` fields,
// which are deliberately still ignored. The server computes `current` against
// its CURRENT_* version regardless of whether ENFORCE_DOCUMENT_VERSIONS is
// armed, so honouring it would have this client re-prompting companies whose
// sessions the backend is happily admitting. The two halves must refuse the
// same people; the shared constant is what keeps them in step, and a flag the
// snapshot cannot see is not something to gate on.
export const isCompanyDpaAccepted = (companyDpa: CompanyDpaInfo): boolean =>
  !!companyDpa?.accepted && isLegalVersionSatisfied(companyDpa.version, REQUIRED_DPA_VERSION);

// 7HAI-972: a DPA is only pending when we actually HOLD a record saying so.
//
// The backend never returns null for a company that has simply not accepted —
// it returns a never-accepted stub ({accepted: null, …}). `null` therefore
// means one of two things, and neither should raise a company-contract gate:
// the caller has no company at all (login leaves `company_dpa` null in that
// case), or this client has not loaded the record yet — a session that predates
// this release, whose stored snapshot has no `companyDpa` key. Treating that
// absence as "not accepted" would strand an ordinary user on "waiting for your
// administrator" for a company that had in fact already accepted, with no way
// out but a re-login. The server-side gate is the authoritative one; this one
// is UX, and must not invent a blocker from missing information.
export const isCompanyDpaPending = (companyDpa: CompanyDpaInfo): boolean =>
  !!companyDpa && !isCompanyDpaAccepted(companyDpa);

// The step is being shown again because the document was UPDATED — an
// acceptance exists, it is just below the required version. Drives the "updated"
// copy on the agreements page; a first-time acceptance is not an update.
export const isPrivacyReacceptance = (user: UserResponseModel | null): boolean =>
  hasPrivacyOnRecord(user) && !isPrivacyAccepted(user);

export const isCompanyEulaReacceptance = (companyLicense: CompanyLicenseInfo): boolean =>
  !!companyLicense?.accepted && !isCompanyEulaAccepted(companyLicense);

export const isCompanyDpaReacceptance = (companyDpa: CompanyDpaInfo): boolean =>
  !!companyDpa?.accepted && !isCompanyDpaAccepted(companyDpa);

// Convenience for the agreements page: is the step in front of the user a
// re-acceptance of a document they had already accepted?
export const isAgreementReacceptance = (
  step: AgreementId,
  user: UserResponseModel | null,
  companyLicense: CompanyLicenseInfo,
  companyDpa: CompanyDpaInfo = null,
): boolean => {
  if (step === "privacy") return isPrivacyReacceptance(user);
  if (step === "license") return isCompanyEulaReacceptance(companyLicense);
  return isCompanyDpaReacceptance(companyDpa);
};

// The company-wide agreements this company still owes, in the order they are
// presented. Only an admin can clear these; a non-admin waits for one.
export const companyWidePending = (
  companyLicense: CompanyLicenseInfo,
  companyDpa: CompanyDpaInfo = null,
): AgreementId[] => {
  const pending: AgreementId[] = [];
  if (!isCompanyEulaAccepted(companyLicense)) pending.push("license");
  if (isCompanyDpaPending(companyDpa)) pending.push("dpa");
  return pending;
};

// Steps the current user can actively ACCEPT on the agreements page (privacy
// for user/admin; the company-wide documents only for admin). master → none.
export const pendingAgreementSteps = (
  user: UserResponseModel | null,
  companyLicense: CompanyLicenseInfo,
  companyDpa: CompanyDpaInfo = null,
): AgreementId[] => {
  if (!user) return [];
  const role = getUserMainRole(user.privileges);
  // Only admin/user accept anything; master is exempt and locked/unknown roles
  // are not gated (they cannot reach the app anyway).
  if (role !== "admin" && role !== "user") return [];
  const steps: AgreementId[] = [];
  if (!isPrivacyAccepted(user)) steps.push("privacy");
  if (role === "admin") steps.push(...companyWidePending(companyLicense, companyDpa));
  return steps;
};

// The company-wide agreements a NON-ADMIN user is blocked behind: they have
// accepted everything they can, and their admin has not accepted these yet.
export const waitingForAdminAgreements = (
  user: UserResponseModel | null,
  companyLicense: CompanyLicenseInfo,
  companyDpa: CompanyDpaInfo = null,
): AgreementId[] => {
  if (!user) return [];
  const role = getUserMainRole(user.privileges);
  if (role !== "user") return [];
  if (!isPrivacyAccepted(user)) return [];
  return companyWidePending(companyLicense, companyDpa);
};

// A non-admin user who has accepted everything they can but is still blocked
// because their company hasn't accepted a company-wide agreement yet.
export const isWaitingForAdmin = (
  user: UserResponseModel | null,
  companyLicense: CompanyLicenseInfo,
  companyDpa: CompanyDpaInfo = null,
): boolean => waitingForAdminAgreements(user, companyLicense, companyDpa).length > 0;

// True when the user must still pass the agreements gate for ANY reason —
// an acceptable step OR waiting for their admin's company-wide acceptance.
export const hasPendingAgreement = (
  user: UserResponseModel | null,
  companyLicense: CompanyLicenseInfo = null,
  companyDpa: CompanyDpaInfo = null,
): boolean => {
  if (!user) return false;
  const role = getUserMainRole(user.privileges);
  // master exempt; locked/unknown not gated (cannot reach the app anyway).
  if (role !== "admin" && role !== "user") return false;
  if (!isPrivacyAccepted(user)) return true;
  // admin → must accept the company-wide documents; user → must wait for one.
  return companyWidePending(companyLicense, companyDpa).length > 0;
};
